import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CookiesPage() {
  const lastUpdated = '2026-06-15';

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="container-page">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-5 tracking-wide">
                LEGAL
              </span>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                Cookie Policy
              </h1>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                How and why Freshhlock uses cookies and similar tracking technologies on our platform.
              </p>
              <p className="text-xs text-foreground-500 mt-4">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="container-page">
            <div className="max-w-3xl mx-auto space-y-10 text-foreground-700 leading-relaxed text-sm md:text-base">

              {/* Section 1 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">1. What Are Cookies?</h2>
                <p>
                  Cookies are small text files that are placed on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how their site is being used.
                </p>
                <p className="mt-3">
                  In addition to cookies, we may use similar technologies such as web beacons (also called pixel tags or clear GIFs), local storage, and session storage (collectively, &ldquo;Tracking Technologies&rdquo;) to achieve the purposes described below.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">2. Types of Cookies We Use</h2>
                <p className="mb-4">Freshhlock uses the following categories of cookies:</p>

                <div className="space-y-5">
                  <div className="p-5 rounded-lg bg-card-50 border border-card-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded bg-accent-100 text-accent-600">
                        <i className="ri-shield-check-line text-sm"></i>
                      </div>
                      <h3 className="font-heading text-base text-foreground-950">Strictly Necessary Cookies</h3>
                    </div>
                    <p className="text-sm">
                      These cookies are essential for the Platform to function and cannot be switched off in our systems. They enable core functionality such as secure login, session management, and load balancing. Without these cookies, services you have requested — like account access or booking submissions — cannot be provided.
                    </p>
                  </div>

                  <div className="p-5 rounded-lg bg-card-50 border border-card-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded bg-accent-100 text-accent-600">
                        <i className="ri-bar-chart-line text-sm"></i>
                      </div>
                      <h3 className="font-heading text-base text-foreground-950">Performance &amp; Analytics Cookies</h3>
                    </div>
                    <p className="text-sm">
                      These cookies help us understand how visitors interact with the Platform by collecting and reporting information anonymously. They tell us which pages are most visited, how users navigate the Platform, and whether they encounter errors — so we can improve performance and user experience.
                    </p>
                  </div>

                  <div className="p-5 rounded-lg bg-card-50 border border-card-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded bg-accent-100 text-accent-600">
                        <i className="ri-settings-3-line text-sm"></i>
                      </div>
                      <h3 className="font-heading text-base text-foreground-950">Functionality Cookies</h3>
                    </div>
                    <p className="text-sm">
                      These cookies allow the Platform to remember choices you make — such as your language preference, dashboard layout customisation, or the region you operate in — and provide enhanced, more personalised features. They may be set by us or by third-party providers whose services we have added to our pages.
                    </p>
                  </div>

                  <div className="p-5 rounded-lg bg-card-50 border border-card-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded bg-accent-100 text-accent-600">
                        <i className="ri-advertisement-line text-sm"></i>
                      </div>
                      <h3 className="font-heading text-base text-foreground-950">Targeting &amp; Advertising Cookies</h3>
                    </div>
                    <p className="text-sm">
                      These cookies may be set through our Platform by our advertising partners. They are used to build a profile of your interests and show you relevant advertisements on other websites. They do not store personal information directly but are based on uniquely identifying your browser and device.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">3. Cookies We Use — Detailed List</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 pr-4 font-semibold text-foreground-900">Cookie Name</th>
                        <th className="text-left py-3 pr-4 font-semibold text-foreground-900">Type</th>
                        <th className="text-left py-3 pr-4 font-semibold text-foreground-900">Duration</th>
                        <th className="text-left py-3 font-semibold text-foreground-900">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/60">
                      <tr>
                        <td className="py-3 pr-4 font-mono text-xs text-foreground-600">fh_session</td>
                        <td className="py-3 pr-4">Necessary</td>
                        <td className="py-3 pr-4">Session</td>
                        <td className="py-3">Maintains your login session while using the Platform</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-mono text-xs text-foreground-600">fh_csrf</td>
                        <td className="py-3 pr-4">Necessary</td>
                        <td className="py-3 pr-4">Session</td>
                        <td className="py-3">Prevents cross-site request forgery attacks</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-mono text-xs text-foreground-600">fh_prefs</td>
                        <td className="py-3 pr-4">Functionality</td>
                        <td className="py-3 pr-4">12 months</td>
                        <td className="py-3">Stores your language and dashboard layout preferences</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-mono text-xs text-foreground-600">_ga, _ga_*</td>
                        <td className="py-3 pr-4">Analytics</td>
                        <td className="py-3 pr-4">24 months</td>
                        <td className="py-3">Google Analytics — measures Platform usage and traffic patterns</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-mono text-xs text-foreground-600">fh_ab</td>
                        <td className="py-3 pr-4">Analytics</td>
                        <td className="py-3 pr-4">30 days</td>
                        <td className="py-3">A/B testing — helps us test and improve new features</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-mono text-xs text-foreground-600">_fbp</td>
                        <td className="py-3 pr-4">Advertising</td>
                        <td className="py-3 pr-4">90 days</td>
                        <td className="py-3">Meta/Facebook pixel — for relevant advertising on Meta platforms</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">4. Third-Party Cookies</h2>
                <p>
                  Some cookies are placed by third-party services that appear on our pages. We use the following third-party services that may set cookies:
                </p>
                <ul className="space-y-2 list-disc pl-5 mt-3">
                  <li><strong className="text-foreground-900">Google Analytics</strong> — for understanding Platform usage and improving user experience. You can opt out via the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">Google Analytics Opt-out Browser Add-on</a>.</li>
                  <li><strong className="text-foreground-900">Meta (Facebook) Pixel</strong> — for measuring advertising effectiveness and reaching potential business users. You can manage your ad preferences through <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">Facebook Ad Preferences</a>.</li>
                  <li><strong className="text-foreground-900">Payment Gateways</strong> — Our payment processors may set cookies for fraud detection and transaction security.</li>
                </ul>
                <p className="mt-3">
                  We do not control third-party cookies. Please refer to the respective third-party privacy policies for information about their cookie practices.
                </p>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">5. How to Manage Cookies</h2>
                <p className="mb-3">
                  You have the right to choose whether to accept cookies. Most web browsers automatically accept cookies, but you can modify your browser settings to decline cookies or alert you when a cookie is being sent. Please note that disabling Strictly Necessary Cookies may impact the functionality and security of the Platform.
                </p>
                <p>Here are links to cookie management instructions for common browsers:</p>
                <ul className="space-y-1.5 list-disc pl-5 mt-2">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/en-in/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">Apple Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">Microsoft Edge</a></li>
                </ul>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">6. Updates to This Policy</h2>
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. We will notify you of material changes through the Platform or via email. We encourage you to review this page periodically.
                </p>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">7. Contact Us</h2>
                <p>If you have any questions about our use of cookies or this Cookie Policy, please contact us:</p>
                <div className="mt-3 p-5 rounded-lg bg-card-50 border border-card-200 space-y-1.5 text-sm shadow-sm">
                  <p><strong className="text-foreground-900">Email:</strong> privacy@freshhlock.in</p>
                  <p><strong className="text-foreground-900">Address:</strong> Freshhlock Technologies Pvt. Ltd., 4th Floor, DLF Cyber City, Gurugram, Haryana — 122002, India</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}